// web/src/hooks/usePipelineEvents.ts — subscribe to real-time pipeline events.
//
// Opens a read-only WebSocket to /ws/events?token=<jwt>. During each agent
// turn the Celery worker publishes PipelineEventFrames to Redis; the backend
// forwards them here. Each frame is dispatched to the conversation store's
// updatePipelineSteps so PipelineSteps components render in real time.
//
// Frame shape expected from backend:
//   { type: "pipeline_event", task_id: str, event: str, step: str,
//     agent?: str, status: "running"|"done"|"error", progress?: float,
//     payload?: {} }
import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/authStore";
import { useConversationStore } from "../stores/conversationStore";
import { useAgentsStore } from "../stores/agentsStore";
import { api } from "../lib/apiClient";
import { WS_RECONNECT_DELAYS_MS, WS_STEP_BUFFER_TTL_MS, WS_CLOSE_AUTH_FAILURE } from "../lib/displayConfig";
export function usePipelineEvents() {
    const token = useAuthStore((s) => s.accessToken);
    const updatePipelineSteps = useConversationStore((s) => s.updatePipelineSteps);
    const setMessageEta = useConversationStore((s) => s.setMessageEta);
    const addAssistantMessage = useConversationStore((s) => s.addAssistantMessage);
    const retriesRef = useRef(0);
    const wsRef = useRef(null);
    const unmountedRef = useRef(false);
    const isRefreshingRef = useRef(false);
    // Accumulate steps per task_id, then flush to the correct agent thread
    const stepBufferRef = useRef(new Map());
    useEffect(() => {
        unmountedRef.current = false;
        if (!token)
            return;
        function connect() {
            if (unmountedRef.current)
                return;
            const freshToken = useAuthStore.getState().accessToken;
            if (!freshToken)
                return;
            const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
            const url = `${protocol}//${window.location.host}/ws/events?token=${encodeURIComponent(freshToken)}`;
            const ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => {
                retriesRef.current = 0;
            };
            ws.onmessage = (ev) => {
                try {
                    const frame = JSON.parse(ev.data);
                    if (frame.type !== "pipeline_event")
                        return;
                    const taskId = frame.task_id ?? "unknown";
                    const eventName = frame.event;
                    // Refresh-resilience: the worker emits `turn_complete` at the
                    // end of every turn. A tab that joined mid-flight (refresh,
                    // new device) won't have its own poll loop running — fetch
                    // the final result and splice it into the pending placeholder.
                    if (eventName === "turn_complete") {
                        const selectedAgentId = useAgentsStore.getState().selectedAgentId;
                        const threads = useConversationStore.getState().threads;
                        const thread = threads[selectedAgentId];
                        const targetAgentId = thread?.taskId === taskId
                            ? selectedAgentId
                            : Object.entries(threads).find(([, t]) => t.taskId === taskId)?.[0] ?? selectedAgentId;
                        (async () => {
                            try {
                                const result = await api.executeResult(taskId);
                                if (result.status === "failed") {
                                    addAssistantMessage(targetAgentId, {
                                        role: "assistant",
                                        content: result.error ??
                                            "Something went wrong processing this request. Please try again.",
                                        agent_id: targetAgentId,
                                        task_id: taskId,
                                    });
                                    return;
                                }
                                const pipelineSteps = (result.trace_steps ?? []).map((s) => ({
                                    step: String(s.step ?? "step"),
                                    agent: s.agent,
                                    status: "done",
                                    llm_input: s.llm_input,
                                    llm_output: s.llm_output,
                                    latency_ms: s.latency_ms,
                                    wall_ms: s.wall_ms,
                                }));
                                addAssistantMessage(targetAgentId, {
                                    role: "assistant",
                                    content: result.reply ?? "(no reply)",
                                    agent_id: targetAgentId,
                                    task_id: taskId,
                                    sub_role: result.sub_role,
                                    confidence: result.confidence,
                                    langfuse_trace_url: result.langfuse_trace_url,
                                    renders: result.renders,
                                    specialists: result.specialists,
                                    answer: result.answer,
                                    narrative: result.narrative,
                                    blocks: result.blocks,
                                    response_template: result.response_template,
                                    executive_summary: result.executive_summary,
                                    headline_metrics: result.headline_metrics,
                                    analysis_narrative: result.analysis_narrative,
                                    inline_renders: result.inline_renders,
                                    executive_situation: result.executive_situation,
                                    executive_recommendation: result.executive_recommendation,
                                    situation_narrative: result.situation_narrative,
                                    balance_sheet: result.balance_sheet,
                                    cash_flow: result.cash_flow,
                                    assumptions: result.assumptions,
                                    assumptions_used: result.assumptions_used,
                                    risks: result.risks,
                                    open_items: result.open_items,
                                    unified_recommendations: result.unified_recommendations,
                                    pipeline_steps: pipelineSteps.length > 0 ? pipelineSteps : undefined,
                                    suggested_prompts: result.suggested_prompts,
                                });
                            }
                            catch {
                                // ignore — the in-page poll loop (if still alive) will
                                // finalize. On refresh, the placeholder will stay
                                // pending until the next turn_complete or a hard reload.
                            }
                        })();
                        return;
                    }
                    // ETA frames carry a predicted-duration payload, not a step.
                    // Route them to setMessageEta and skip the step buffer.
                    if (eventName === "eta") {
                        const payload = frame.payload ?? {};
                        const seconds = Number(payload.seconds ?? 0);
                        const n = Number(payload.n ?? 0);
                        if (seconds > 0) {
                            const selectedAgentId = useAgentsStore.getState().selectedAgentId;
                            const thread = useConversationStore.getState().threads[selectedAgentId];
                            const targetAgentId = thread?.taskId === taskId
                                ? selectedAgentId
                                : Object.entries(useConversationStore.getState().threads).find(([, t]) => t.taskId === taskId)?.[0] ?? selectedAgentId;
                            setMessageEta(targetAgentId, taskId, seconds, n);
                        }
                        return;
                    }
                    const stepName = frame.step ?? eventName ?? "step";
                    const agentId = frame.agent;
                    const status = frame.status ?? "running";
                    const detail = frame.detail;
                    // Build / update the step buffer for this task
                    const buffer = stepBufferRef.current.get(taskId) ?? [];
                    const existingIdx = buffer.findIndex((s) => s.step === stepName);
                    const updatedStep = { step: stepName, agent: agentId, status, detail };
                    if (existingIdx >= 0) {
                        buffer[existingIdx] = updatedStep;
                    }
                    else {
                        buffer.push(updatedStep);
                    }
                    stepBufferRef.current.set(taskId, buffer);
                    // Find which agent thread owns this task_id
                    const selectedAgentId = useAgentsStore.getState().selectedAgentId;
                    const thread = useConversationStore.getState().threads[selectedAgentId];
                    // Match task to the thread that owns it
                    const targetAgentId = thread?.taskId === taskId
                        ? selectedAgentId
                        : Object.entries(useConversationStore.getState().threads).find(([, t]) => t.taskId === taskId)?.[0] ?? selectedAgentId;
                    // Pass taskId so the store can attach steps to the in-flight
                    // assistant placeholder (added on user submit) instead of
                    // writing to the most-recent message (which would be the user
                    // turn — the old bug).
                    updatePipelineSteps(targetAgentId, [...buffer], taskId);
                    // If this is a terminal event, clean up buffer
                    if (status === "done" || status === "error") {
                        const allDone = buffer.every((s) => s.status === "done" || s.status === "error");
                        if (allDone) {
                            setTimeout(() => stepBufferRef.current.delete(taskId), WS_STEP_BUFFER_TTL_MS);
                        }
                    }
                }
                catch {
                    // Malformed frame — ignore
                }
            };
            ws.onclose = (event) => {
                wsRef.current = null;
                if (unmountedRef.current)
                    return;
                if (event.code === WS_CLOSE_AUTH_FAILURE) {
                    // Auth failure — token expired or invalid. Refresh before reconnecting.
                    // Guard against concurrent refresh calls (e.g. two rapid 1008 closes).
                    if (isRefreshingRef.current)
                        return;
                    isRefreshingRef.current = true;
                    retriesRef.current = 0;
                    useAuthStore.getState().refresh().then((ok) => {
                        isRefreshingRef.current = false;
                        if (!ok) {
                            // Refresh token also expired — force re-login.
                            useAuthStore.getState().logout();
                            return;
                        }
                        if (!unmountedRef.current)
                            connect();
                    });
                    return;
                }
                // Normal disconnect — backoff reconnect with current token.
                const delay = WS_RECONNECT_DELAYS_MS[Math.min(retriesRef.current, WS_RECONNECT_DELAYS_MS.length - 1)];
                retriesRef.current++;
                setTimeout(connect, delay);
            };
            ws.onerror = () => {
                ws.close();
            };
        }
        connect();
        return () => {
            unmountedRef.current = true;
            wsRef.current?.close();
            wsRef.current = null;
        };
        // Intentionally empty: the WS lifecycle is self-managed via onclose.
        // connect() reads the token via useAuthStore.getState() at call time so
        // it always uses the freshest value. Listing token here would tear down
        // and recreate the socket on every refresh, racing with the onclose
        // auth-refresh path.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
